import React, { useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { AUTH_API } from '../../constants';
import { signupOverlayTheme } from '../../theme';
import OverlayContentHeader from './OverlayContentHeader';
import SignupOverlayWrapper from 'SharedComponents/SignupOverlayWrapper';
import LoginRegisterForm from 'SharedComponents/LoginRegisterForm';
import SignupOverlayProps, { MultiStepRegistrationProps } from 'Interfaces/SignupOverlayProps';
import { OverlayDismissLabel } from 'Interfaces/overlayTracking';
import { OverlayHeader } from 'SharedComponents/styled/overlay/OverlayHeader';
import { OverlayContent } from 'SharedComponents/styled/overlay/OverlayContent';
import { CrossButton } from 'SharedComponents/styled/overlay/CrossButton';
import useSignupFormTracking from 'Hooks/useSignupFormTracking';
import useLoginFormTracking from 'Hooks/useLoginFormTracking';
import Cross from 'SharedComponents/Cross';
import { BodyBlockerGlobalStyle } from 'SharedComponents/styled/overlay/BodyBlockerGlobalStyle';
import { Overlay } from 'SharedComponents/styled/overlay/Overlay';
import { OverlayContainer } from 'SharedComponents/styled/overlay/OverlayContainer';
import { Backdrop } from 'SharedComponents/styled/overlay/Backdrop';
import { useFeatureToggle } from 'AppShell/hooks';

const SignupOverlay: React.FunctionComponent<SignupOverlayProps & Partial<MultiStepRegistrationProps>> = ({
    ...overlayProps
}) => {
    const props = {
        isClosable: false,
        hideSwitch: false,
        showLoginAtStart: false,
        animatedSubmitButtonCta: false,
        animateEmailPlaceholder: false,
        logo: undefined,
        focusSignupFormEmail: false,
        ...overlayProps,
    };

    const { p100EnforceCloseButtonOnShop2ClubOverlay } = useFeatureToggle();
    const [showLogin, setShowLogin] = useState(props.showLoginAtStart);
    const { trackOverlayDismiss, trackFormUnfolded } = useSignupFormTracking();
    const [isLastStep, setIsLastStep] = useState(false);
    const [isSignupFormFolded, setIsSignupFormFolded] = useState(true);
    const [activeStep, setActiveStep] = useState(1);

    const { trackOverlayShown: trackSignupShown } = useSignupFormTracking();
    const { trackOverlayShown: trackLoginShown } = useLoginFormTracking();

    const onOverlayContainerClick = (event: React.MouseEvent) => {
        if (props.isClosable && event.target === event.currentTarget) {
            trackOverlayDismiss(OverlayDismissLabel.Background);
            props.handleClose();
        }
    };

    useEffect(() => {
        if (props.partialLoginEmail !== '') {
            setShowLogin(true);
        }
    }, [props.partialLoginEmail]);

    useEffect(() => {
        if (props.isOpen) {
            if (showLogin) {
                trackLoginShown(props.trackingLabel);
            } else {
                trackSignupShown(props.trackingLabel);
            }
        }
    }, [props.isOpen, props.trackingLabel, showLogin, trackLoginShown, trackSignupShown]);

    const onCloseButtonClick = () => {
        trackOverlayDismiss(OverlayDismissLabel.CloseButton);
        props.handleClose();
    };

    const onToggleLogin = () => {
        if (showLogin) {
            trackOverlayDismiss(OverlayDismissLabel.RegisterLink);
        } else {
            trackOverlayDismiss(OverlayDismissLabel.LoginLink);
        }
        setShowLogin(!showLogin);
    };

    const onFormFoldChange = (folded: boolean) => {
        if (!folded) {
            trackFormUnfolded();
        }
        setIsSignupFormFolded(folded);
    };

    const isCloseButtonVisible = () => p100EnforceCloseButtonOnShop2ClubOverlay || (props.isClosable && showLogin);

    return (
        <SignupOverlayWrapper isOpen={props.isOpen}>
            {props.isOpen && <BodyBlockerGlobalStyle />}
            <Backdrop disablePointerEvents={false} />
            <OverlayContainer lessPadding={false} onClick={props.isClosable ? onOverlayContainerClick : undefined}>
                <Overlay backgroundColor={props.backgroundColor} isBig={false} isShifted={false} isLogin={showLogin}>
                    <OverlayHeader isVisible>
                        <CrossButton
                            onClick={onCloseButtonClick}
                            isVisible={isCloseButtonVisible()}
                            isVisibleOnMobile={isCloseButtonVisible()}
                            className="qa-close-button"
                        >
                            <Cross />
                        </CrossButton>
                    </OverlayHeader>
                    <OverlayContent>
                        <OverlayContentHeader isLogin={showLogin} />
                        <ThemeProvider theme={signupOverlayTheme}>
                            <LoginRegisterForm
                                isSignupFormFolded={isSignupFormFolded}
                                setIsSignupFormFolded={onFormFoldChange}
                                showLogin={showLogin}
                                handleShowLoginToggle={onToggleLogin}
                                apiBaseUrl={AUTH_API}
                                redirectUrl={props.redirectUrl}
                                isMagicLinkEnabled={props.isMagicLinkEnabled}
                                mailcheckOptions={props.mailcheckOptions}
                                formIdentifiers={props.formIdentifiers}
                                privacyPolicy={props.privacyPolicy}
                                isMultiStep={props.isMultiStep}
                                isLastStep={isLastStep}
                                setIsLastStep={setIsLastStep}
                                activeStep={activeStep}
                                setActiveStep={setActiveStep}
                                setIsFormReady={props.setIsFormReady}
                                isFormReady={props.isFormReady}
                                animatedSubmitButtonCta={props.animatedSubmitButtonCta}
                                animateEmailPlaceholder={props.animateEmailPlaceholder}
                                isReferral={props.isReferral}
                                defaultEmail={props.partialLoginEmail}
                                focusSignupFormEmail={props.focusSignupFormEmail}
                                forgetPasswordRedirectUrl={props.forgetPasswordRedirectUrl}
                                // TODO Do we need props drilling?
                                handleClose={props.handleClose}
                            />
                        </ThemeProvider>
                    </OverlayContent>
                </Overlay>
            </OverlayContainer>
        </SignupOverlayWrapper>
    );
};

export default SignupOverlay;
